import { useCallback, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";

/**
 * The floating call window's drag behaviour, extracted from `CallWindow.tsx`.
 *
 * The call is a FLOATING, non-modal widget (default bottom-right) so the rest
 * of the app stays usable. Drag it by the header. `pos` is null until first
 * dragged (then it pins top/left in px); we clamp to the viewport. The window
 * is centered (not draggable) while `maximized`. `resetPos` returns to the
 * default anchor — the page calls it when a call clears.
 */
export interface DraggableWindow {
  winRef: React.RefObject<HTMLDivElement | null>;
  /** Pinned top/left in px once dragged, else `null` (default anchor). */
  pos: { x: number; y: number } | null;
  resetPos: () => void;
  onDragStart: (e: ReactPointerEvent<HTMLDivElement>) => void;
  onDragMove: (e: ReactPointerEvent<HTMLDivElement>) => void;
  onDragEnd: () => void;
}

export function useDraggableWindow(maximized: boolean): DraggableWindow {
  const winRef = useRef<HTMLDivElement>(null);
  const dragOffsetRef = useRef<{ dx: number; dy: number } | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  const onDragStart = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (maximized) return; // expanded view is centered, not draggable
    const rect = winRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragOffsetRef.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    setPos({ x: rect.left, y: rect.top });
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, [maximized]);

  const onDragMove = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    const off = dragOffsetRef.current;
    if (!off) return;
    const w = winRef.current?.offsetWidth ?? 340;
    const h = winRef.current?.offsetHeight ?? 200;
    const x = Math.max(8, Math.min(e.clientX - off.dx, window.innerWidth - w - 8));
    const y = Math.max(8, Math.min(e.clientY - off.dy, window.innerHeight - h - 8));
    setPos({ x, y });
  }, []);

  const onDragEnd = useCallback(() => { dragOffsetRef.current = null; }, []);

  const resetPos = useCallback(() => setPos(null), []);

  return { winRef, pos, resetPos, onDragStart, onDragMove, onDragEnd };
}
